
import { useState } from 'react';
import { faqItems, faqCategories } from '../../../mocks/faqData';

// IDs of the most popular questions
const popularIds = [1, 7, 34, 28, 15];

/**
 * Build a map of category id → { label, icon } for quick lookup.
 * The original code used `Record<string, …>` while `cat.id` is a number,
 * which caused a TypeScript parsing error (`Expected identifier but found "/"`).
 * Switching the key type to `number` resolves the issue and keeps the map type‑safe.
 */
const categoryMap: Record<number, { label: string; icon: string }> = {};
faqCategories.forEach((cat) => {
  categoryMap[cat.id] = { label: cat.label, icon: cat.icon };
});

/**
 * Resolve the list of popular items.
 * Guard against a missing item (e.g., if an ID is not present in `faqItems`)
 * by filtering out `undefined` values.
 */
const popularItems = popularIds
  .map((id) => faqItems.find((item) => item.id === id))
  .filter((item): item is typeof faqItems[0] => Boolean(item));

export default function PopularQuestions() {
  const [openId, setOpenId] = useState<number | null>(null);

  return (
    <section className="max-w-4xl mx-auto px-6 pt-10 pb-2">
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-full">
            <i className="ri-fire-line text-lg text-amber-600"></i>
          </div>
          <h2 className="text-xl font-bold text-gray-900 tracking-wide">
            Most Popular Questions
          </h2>
        </div>
        <p className="text-sm text-gray-500 ml-[42px]">
          The questions farmers and landowners ask us most often
        </p>
      </div>

      <div className="grid gap-3">
        {popularItems.map((item, idx) => {
          // Fallback to a generic label/icon if the category is unknown
          const cat =
            categoryMap[item.category] ?? {
              label: item.category.toString(),
              icon: 'ri-question-line',
            };
          const isOpen = openId === item.id;

          return (
            <div
              key={item.id}
              className="bg-white rounded-lg border border-amber-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              <button
                onClick={() => setOpenId(isOpen ? null : item.id)}
                className="w-full px-5 py-4 text-left flex items-start gap-4 cursor-pointer hover:bg-amber-50/40 transition-colors"
                aria-expanded={isOpen}
                aria-controls={`answer-${item.id}`}
              >
                <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-amber-50 text-amber-700 font-bold text-xs mt-0.5">
                  {idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${cat.icon} text-emerald-600 text-xs`}></i>
                    </div>
                    <span className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">
                      {cat.label}
                    </span>
                  </div>
                  <span className="font-bold text-gray-900 leading-snug block text-[15px]">
                    {item.question}
                  </span>
                </div>
                <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5 bg-amber-50 rounded-full">
                  <i
                    className={`ri-arrow-down-s-line text-lg text-amber-700 transition-transform duration-300 ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </div>
              </button>

              {/* Answer panel – hidden/shown with a smooth height transition */}
              <div
                id={`answer-${item.id}`}
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  isOpen ? 'max-h-[500px]' : 'max-h-0'
                }`}
                aria-hidden={!isOpen}
              >
                <div className="px-5 pb-5 pl-16 text-sm text-gray-700 leading-relaxed border-t border-amber-100 pt-4">
                  {item.answer}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
